// TODO: для прода - access/refresh токены в httpOnly куки

import axios, { AxiosError, AxiosInstance, AxiosProgressEvent } from "axios";
import { parseApiError } from "src/utils/errors/errorParser";
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ClientsListParams,
  ClientsListResponse,
  StatesListResponse,
  ClientDetailResponse,
  ClientProfileResponse,
  ClientLinkDetailResponse,
  AccountsListParams,
  AccountsListResponse,
  TransactionsListParams,
  TransactionsListResponse,
  ProfileListResponse,
  EmailParams,
  FoundClient,
  CreateLinkResponse,
  ClientIdParams,
  ClientDetailRequest,
  CompanyType,
  Industry,
  State,
  CompanyResponse,
  RulesListParams,
  RulesListResponse,
  ClientArchieveResponse,
  CreateAccountRequest,
  AccountResponse,
  BanksListResponse,
  Company,
  CreateCategoryRequest,
  CreateCategoryResponse,
  CategoriesListResponse,
  CreateTransactionRequest,
  UpdateTransactionRequest,
  TransactionResponse,
  AlertsListParams,
  AlertsListResponse,
  AlertItem,
  CreateRuleRequest,
  CreateRuleResponse,
  UpdateRuleRequest,
  UpdateRuleResponse,
  ReportParams,
  ReportResponse,
  ReportCompareParams,
  ReportCompareResponse,
  CreateAssetRequest,
  AssetResponse,
  AssetClassResponse,
  AssetDepreciationResponse,
  AssetsListResponse,
  CreateLiabilityRequest,
  LiabilitiesListResponse,
  LiabilityAllocationRequest,
  LiabilityAllocationResponse,
  TransactionLiabilityCandidateResponse,
  LiabilityResponse,
  LiabilityScheduleEntryResponse,
  AccountSummaryResponse,
  GoogleAuthResponse,
  GoogleCredentialRequest,
  GoogleRegistrationCompleteRequest,
  GoogleAuthenticatedResponse,
  BackgroundJob,
  BackgroundJobsListParams,
  BackgroundJobsListResponse,
  AccountBalanceSnapshot,
  CompanyOpeningEquity,
  RulePreviewResponse,
  UncategorizedGroupItem,
  UncategorizedGroupApplyRequest,
  UncategorizedGroupApplyResponse,
  PlaidLinkSessionOut,
  CreatePlaidLinkSessionRequest,
  PlaidItemOut,
  PlaidAccountOut,
  PlaidMapAccountsRequest,
  PlaidSyncResponse,
  PlaidConnectLinkOut,
  PlaidConnectConsentOut,
} from "./types";

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, "");

const BASE_URL = stripTrailingSlash(
  process.env.REACT_APP_BACKEND_URL || "https://pinfin.app/api/v1",
);
const REFRESH_TOKEN_PATH = "/token/refresh";
const PUBLIC_AUTH_PATHS = [
  "/token/pair",
  "/register",
  "/forgot-password",
  "/auth/google",
  "/auth/google/complete",
];
const PUBLIC_AUTH_PREFIXES = [
  "/verify-email/",
  "/reset-password/",
  "/plaid/connect/",
];

const getRequestPath = (url?: string) => {
  if (!url) return "";

  try {
    return new URL(url, BASE_URL).pathname;
  } catch {
    return url;
  }
};

const pathMatches = (path: string, target: string) => path === target || path.endsWith(target);

const isRefreshTokenRequest = (url?: string) => {
  const path = getRequestPath(url);
  return pathMatches(path, REFRESH_TOKEN_PATH);
};

const isPublicAuthRequest = (url?: string) => {
  const path = getRequestPath(url);

  return (
    PUBLIC_AUTH_PATHS.some((target) => pathMatches(path, target)) ||
    PUBLIC_AUTH_PREFIXES.some((target) => path.includes(target))
  );
};

//TODO!!
export class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{ resolve: (token: string) => void; reject: (e?: unknown) => void }> = [];
  private logoutCallback?: () => void;

  constructor(onLogout?: () => void) {
    this.logoutCallback = onLogout;

    this.client = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
      timeout: 30000,
      headers: { "Content-Type": "application/json" },
      paramsSerializer: (params) => {
        const parts: string[] = [];
        for (const [key, val] of Object.entries(params)) {
          if (val === undefined || val === null) continue;
          if (Array.isArray(val)) {
            val.forEach((v) => parts.push(`${key}=${encodeURIComponent(v)}`));
          } else {
            parts.push(`${key}=${encodeURIComponent(String(val))}`);
          }
        }
        return parts.join("&");
      },
    });

    this.setupInterceptors();
  }

  isProtectedMediaSource(source: string): boolean {
    try {
      const path = new URL(source, window.location.origin).pathname;
      return path.startsWith("/media/");
    } catch {
      return source.startsWith("/media/");
    }
  }

  async getProtectedMedia(source: string): Promise<Blob> {
    const path = new URL(source, window.location.origin).pathname;
    const mediaKey = decodeURIComponent(path.slice("/media/".length));
    return (
      await this.client.get("/media", {
        params: { path: mediaKey },
        responseType: "blob",
      })
    ).data;
  }

  private setupInterceptors() {
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem("access_token");
      if (token && config.headers && !isPublicAuthRequest(config.url)) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (res) => {
        if (process.env.NODE_ENV === "development") {
          console.log("API Response:", res.status, res.config.url, res.data);
        }
        return res;
      },
      (error) => {
        if (process.env.NODE_ENV === "development") {
          console.error("API Error:", error.response?.status, error.config?.url, error.response?.data);
        }
        return this.handleResponseError(error);
      }
    );
  }

  private handleResponseError = async (error: AxiosError) => {
    const original = error.config as any;

    // Если сам refresh-запрос вернул 401 — токен протух, сразу логаут (без retry)
    if (error.response?.status === 401 && isRefreshTokenRequest(original?.url)) {
      this.isRefreshing = false;
      this.processQueue(error);
      this.triggerLogout();
      throw parseApiError(error);
    }

    if (isPublicAuthRequest(original?.url)) {
      throw parseApiError(error);
    }

    if (error.response?.status !== 401 || original._retry) {
      throw parseApiError(error);
    }

    original._retry = true;

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (original.headers) original.headers.Authorization = `Bearer ${token}`;
        return this.client(original);
      });
    }

    this.isRefreshing = true;

    const refresh = localStorage.getItem("refresh_token");
    if (!refresh) {
      this.triggerLogout();
      throw parseApiError(error);
    }

    try {
      const { access, refresh: newRefresh } = await this.refreshToken({ refresh });
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", newRefresh);

      this.processQueue(null, access);

      if (original.headers) original.headers.Authorization = `Bearer ${access}`;
      return this.client(original);
    } catch (err) {
      this.processQueue(err);
      this.triggerLogout();
      if (err instanceof AxiosError) {
        throw parseApiError(err);
      }
      throw err;
    } finally {
      this.isRefreshing = false;
    }
  };

  private processQueue(error: unknown, token?: string) {
    this.failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
    this.failedQueue = [];
  }

  setLogoutCallback(cb: () => void) {
    this.logoutCallback = cb;
  }

  private triggerLogout() {
    if (this.logoutCallback) {
      this.logoutCallback();
    } else {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      window.location.href = "/login";
    }
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    return (await this.client.post("/token/pair", data)).data;
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    return (await this.client.post("/register", data)).data;
  }

  async authenticateGoogle(data: GoogleCredentialRequest): Promise<GoogleAuthResponse> {
    return (await this.client.post("/auth/google", data)).data;
  }

  async completeGoogleRegistration(
    data: GoogleRegistrationCompleteRequest,
  ): Promise<GoogleAuthenticatedResponse> {
    return (await this.client.post("/auth/google/complete", data)).data;
  }

  async verifyEmail(uuid: string): Promise<void> {
    await this.client.get(`/verify-email/${uuid}`);
  }

  async resendVerification(data: ForgotPasswordRequest): Promise<void> {
    await this.client.post("/resend-verification", data);
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    await this.client.post("/forgot-password", data);
  }

  async resetPassword(uuid: string, data: ResetPasswordRequest): Promise<void> {
    await this.client.post(`/reset-password/${uuid}`, data);
  }

  async refreshToken(data: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    return (await this.client.post("/token/refresh", data)).data;
  }

  async getClients(params: ClientsListParams = {}): Promise<ClientsListResponse> {
    return (await this.client.get("/links/clients", { params })).data;
  }
  
  async getStates(): Promise<StatesListResponse> {
    return (await this.client.get("/states")).data;
  }

  async getClientDetail(clientId: number): Promise<ClientDetailResponse> {
    const response = (
      await this.client.get<ClientLinkDetailResponse>(
        `/links/clients/${clientId}`
      )
    ).data;
    return {
      ...response.client,
      link_id: response.link_id,
      status: response.status,
    };
  }

  async getAccounts(params: AccountsListParams = {}): Promise<AccountsListResponse> {
    return (await this.client.get("/accounts", { params })).data;
  }

  async getTransactions(params: TransactionsListParams = {
    account_id: null
  }): Promise<TransactionsListResponse> {
    return (await this.client.get("/transactions", { params })).data;
  }
  
  async getProfile(params: TransactionsListParams = {
    account_id: null
  }): Promise<ProfileListResponse> {
    return (await this.client.get("/profile", { params })).data;
  }

   async updateProfile(formData: FormData): Promise<ProfileListResponse> {
    const response = await this.client.patch("/profile", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async findProFileByEmail(params: EmailParams): Promise<FoundClient> {
    return (await this.client.get("/profile/find", {params})).data;
  }

  async createLink(params: ClientIdParams): Promise<CreateLinkResponse> {
    return (await this.client.post("/links", params)).data;
  }

  async approveLink(linkId: number): Promise<CreateLinkResponse> {
    return (await this.client.post(`/links/${linkId}/approve`)).data;
  }

  async denyLink(linkId: number): Promise<CreateLinkResponse> {
    return (await this.client.post(`/links/${linkId}/deny`)).data;
  }

  async createClient(params: ClientDetailRequest): Promise<ClientProfileResponse> {
    return (await this.client.post('/links/clients', params)).data;
  }

  async getCompaniesTypes(): Promise<CompanyType[]> {
    return (await this.client.get("/company/types")).data;
  }

  async getIndustries(): Promise<Industry[]> {
    return (await this.client.get("/company/industries")).data;
  }

  async getCompanyStates(): Promise<State[]> {
    return (await this.client.get("/states")).data;
  }

  async createCompany(data: {
    name: string;
    type_id: number | null;
    industry_id: number;
    state_id: number | null;
    user_id: number;
  }): Promise<CompanyResponse> {
    return (await this.client.post("/company", data)).data;
  }

  async getRules(params: RulesListParams = {}): Promise<RulesListResponse> {
    const queryParams: any = { ...params };
    
    if (params.orders && Array.isArray(params.orders)) {
      queryParams.orders = params.orders.join(',');
    }
    
    return (await this.client.get("/rules", { params: queryParams })).data;
  }

  async updateClient(clientId: number, params: ClientDetailRequest): Promise<ClientProfileResponse> {
    return (await this.client.put(`/links/clients/${clientId}`, params)).data;
  }

  async archiveClient(linkId: number): Promise<ClientArchieveResponse> {
    return (await this.client.post(`/links/${linkId}/archieve`)).data
  }

  async unarchiveClient(linkId: number): Promise<ClientArchieveResponse> {
    return (await this.client.post(`/links/${linkId}/unarchieve`)).data
  }

  async createAccount(data: CreateAccountRequest): Promise<AccountResponse> {
  return (await this.client.post("/accounts", data)).data;
  }

  async getBanks(): Promise<BanksListResponse> {
  return (await this.client.get("/banks")).data;
  }

  async getBankTypes(bankId: number): Promise<{ type: string; bank_id: number; is_personal: boolean }[]> {
    return (await this.client.get(`/banks/${bankId}/types`)).data;
  }

  async getUserCompanies(userId: number): Promise<Company[]> {
  return (await this.client.get("/company", { params: { user_id: userId } })).data;
  }

  async getAccountSummary(accountId: number): Promise<AccountSummaryResponse> {
    return (await this.client.get(`/accounts/${accountId}/summary`)).data;
  }

  async getBalanceSnapshots(accountId: number): Promise<AccountBalanceSnapshot[]> {
    return (await this.client.get(`/accounts/${accountId}/balance-snapshots`)).data;
  }

  async saveBalanceSnapshot(
    accountId: number,
    data: { as_of_date: string; balance: number },
    snapshotId?: number,
  ): Promise<AccountBalanceSnapshot> {
    const path = snapshotId
      ? `/accounts/${accountId}/balance-snapshots/${snapshotId}`
      : `/accounts/${accountId}/balance-snapshots`;
    return (await (snapshotId ? this.client.put(path, data) : this.client.post(path, data))).data;
  }

  async deleteBalanceSnapshot(accountId: number, snapshotId: number): Promise<void> {
    await this.client.delete(`/accounts/${accountId}/balance-snapshots/${snapshotId}`);
  }

  async getCompanyOpeningEquity(companyId: number): Promise<CompanyOpeningEquity> {
    return (await this.client.get(`/company/${companyId}/opening-equity`)).data;
  }

  async saveCompanyOpeningEquity(
    companyId: number,
    data: { as_of_date: string; paid_in_capital: number; retained_earnings: number },
  ): Promise<CompanyOpeningEquity> {
    return (await this.client.put(`/company/${companyId}/opening-equity`, data)).data;
  }

  async uploadTransactionFile(
    accountId: number,
    files: File[],
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<BackgroundJob[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        account_id: accountId
      },
      onUploadProgress
    };

    return (await this.client.post("/transaction/file", formData, config)).data;
  }

    async getCategories(params: {
    page?: number;
    page_size?: number;
    max_page_size?: number;
    orders?: string[];
    path__startswith?: string;
    industry_id?: number;
    user_id?: number;
    query?: string;
  } = {}): Promise<CategoriesListResponse> {
    return (await this.client.get("/category", { params })).data;
  }

  async createCategory(data: CreateCategoryRequest): Promise<CreateCategoryResponse> {
    return (await this.client.post("/category", data)).data;
  }

  async deleteCategory(categoryId: number): Promise<void> {
    await this.client.delete(`/category/${categoryId}`);
  }

  async createTransaction(data: CreateTransactionRequest): Promise<TransactionResponse> {
    return (await this.client.post("/transactions", data)).data;
  }

  async updateTransaction(transactionId: number, data: UpdateTransactionRequest): Promise<TransactionResponse> {
    return (await this.client.patch(`/transactions/${transactionId}`, data)).data;
  }

  async bulkUpdateTransactions(data: {
    transaction_ids: number[];
    transaction: {
      category_id: number | null;
    };
  }): Promise<any> {
    return (await this.client.post("/transactions/bulk_update", data)).data;
  }

  async bulkDeleteTransactions(transactionIds: number[]): Promise<{ count: number }> {
    return (
      await this.client.post("/transactions/bulk_delete", {
        transaction_ids: transactionIds,
      })
    ).data;
  }

  async bulkClearTransactions(transactionIds: number[]): Promise<{
    count: number;
    matches_cleared: number;
    allocations_deleted: number;
  }> {
    return (
      await this.client.post("/transactions/bulk_clear", {
        transaction_ids: transactionIds,
      })
    ).data;
  }

  async createRule(data: CreateRuleRequest): Promise<CreateRuleResponse> {
    return (await this.client.post("/rules", data)).data;
  }

  async updateRule(ruleId: number, data: UpdateRuleRequest): Promise<UpdateRuleResponse> {
    return (await this.client.patch(`/rules/${ruleId}`, data)).data;
  }

  async reapplyRule(ruleId: number): Promise<BackgroundJob> {
    return (await this.client.post(`/rules/${ruleId}/reapply`)).data;
  }

  async deleteRule(ruleId: number): Promise<BackgroundJob> {
    return (await this.client.delete(`/rules/${ruleId}`)).data;
  }

  // 200 = уже есть активный job этого клиента (вернулся он же), 202 = создан новый
  async applyAllRules(clientId: number): Promise<{ job: BackgroundJob; alreadyRunning: boolean }> {
    const response = await this.client.post("/rules/apply_all", null, {
      params: { client_id: clientId },
    });
    return { job: response.data, alreadyRunning: response.status === 200 };
  }

  async previewRule(data: CreateRuleRequest): Promise<RulePreviewResponse> {
    return (await this.client.post("/rules/preview", data)).data;
  }

  async getUncategorizedGroups(clientId: number): Promise<UncategorizedGroupItem[]> {
    return (await this.client.get("/transactions/uncategorized_groups", {
      params: { client_id: clientId },
    })).data;
  }

  async applyUncategorizedGroup(
    data: UncategorizedGroupApplyRequest,
  ): Promise<UncategorizedGroupApplyResponse> {
    return (await this.client.post("/transactions/uncategorized_groups/apply", data)).data;
  }

  async getReport(params: ReportParams): Promise<ReportResponse> {
    return (await this.client.get("/report", { params })).data;
  }

  async compareReport(params: ReportCompareParams): Promise<ReportCompareResponse> {
    // Serialize array params manually since axios encodes arrays as foo[]=bar
    const sp = new URLSearchParams();
    sp.set("report_type", params.report_type);
    sp.set("user_id", String(params.user_id));
    if (params.date_from) sp.set("date_from", params.date_from);
    if (params.date_to) sp.set("date_to", params.date_to);
    if (params.as_of_date) sp.set("as_of_date", params.as_of_date);
    sp.set("compare_period", params.compare_period);
    if (params.company_id) sp.set("company_id", String(params.company_id));
    if (params.account_id__in) sp.set("account_id__in", params.account_id__in);
    if (params.compare_date_from) sp.set("compare_date_from", params.compare_date_from);
    if (params.compare_date_to) sp.set("compare_date_to", params.compare_date_to);
    if (params.compare_as_of_date) {
      sp.set("compare_as_of_date", params.compare_as_of_date);
    }
    if (params.accounting_basis) sp.set("accounting_basis", params.accounting_basis);
    params.calculations.forEach((c) => sp.append("calculations", c));
    return (await this.client.get(`/report/compare?${sp.toString()}`)).data;
  }

  async getAlerts(params: AlertsListParams = {}): Promise<AlertsListResponse> {
    return (await this.client.get("/", { params })).data;
  }

  async readAlert(alertId: number): Promise<AlertItem> {
    return (await this.client.patch(`/${alertId}/read`)).data;
  }

  async readAllAlerts(): Promise<string> {
    return (await this.client.post("/read-all")).data;
  }

  async getBackgroundJobs(
    params: BackgroundJobsListParams = {},
  ): Promise<BackgroundJobsListResponse> {
    return (await this.client.get("/jobs", { params })).data;
  }

  async getBackgroundJob(jobUuid: string): Promise<BackgroundJob> {
    return (await this.client.get(`/jobs/${jobUuid}`)).data;
  }

  async cancelBackgroundJob(jobUuid: string): Promise<BackgroundJob> {
    return (await this.client.post(`/jobs/${jobUuid}/cancel`)).data;
  }

  async createAsset(data: CreateAssetRequest): Promise<AssetResponse> {
    return (await this.client.post("/asset", data)).data;
  }

  async getAssetClasses(): Promise<AssetClassResponse[]> {
    return (await this.client.get("/asset/classes")).data;
  }

  async getAssets(params: {
    page?: number;
    page_size?: number;
    query?: string;
    client_id?: number;
    status?: "active" | "disposed";
  } = {}): Promise<AssetsListResponse> {
    return (await this.client.get("/asset", { params })).data;
  }

  async getAsset(assetId: number): Promise<AssetResponse> {
    return (await this.client.get(`/asset/${assetId}`)).data;
  }

  async deleteAsset(assetId: number): Promise<void> {
    await this.client.delete(`/asset/${assetId}`);
  }

  async getAssetDepreciation(
    assetId: number,
    asOfDate: string,
  ): Promise<AssetDepreciationResponse> {
    return (
      await this.client.get(`/asset/${assetId}/depreciation`, {
        params: { as_of_date: asOfDate },
      })
    ).data;
  }

  async createLiability(data: CreateLiabilityRequest): Promise<LiabilityResponse> {
    return (await this.client.post("/liability", data)).data;
  }

  async getLiabilities(params: {
    page?: number;
    page_size?: number;
    query?: string;
    company_id?: number;
    status?: string;
    liability_type?: string;
  } = {}): Promise<LiabilitiesListResponse> {
    return (await this.client.get("/liability", { params })).data;
  }

  async getLiability(liabilityId: number): Promise<LiabilityResponse> {
    return (await this.client.get(`/liability/${liabilityId}`)).data;
  }

  async updateLiability(
    liabilityId: number,
    data: Partial<Omit<CreateLiabilityRequest, "company_id">> & { status?: string },
  ): Promise<LiabilityResponse> {
    return (await this.client.patch(`/liability/${liabilityId}`, data)).data;
  }

  async deleteLiability(liabilityId: number): Promise<void> {
    await this.client.delete(`/liability/${liabilityId}`);
  }

  async getLiabilitySchedule(
    liabilityId: number,
  ): Promise<LiabilityScheduleEntryResponse[]> {
    return (await this.client.get(`/liability/${liabilityId}/schedule`)).data;
  }

  async createLiabilityAllocation(
    liabilityId: number,
    data: LiabilityAllocationRequest,
  ): Promise<LiabilityAllocationResponse> {
    return (await this.client.post(`/liability/${liabilityId}/allocations`, data)).data;
  }

  async getTransactionLiabilityAllocations(
    transactionId: number,
  ): Promise<LiabilityAllocationResponse[]> {
    return (
      await this.client.get(`/transactions/${transactionId}/liability-allocations`)
    ).data;
  }

  async getTransactionLiabilityCandidates(
    transactionId: number,
  ): Promise<TransactionLiabilityCandidateResponse[]> {
    return (
      await this.client.get(`/transactions/${transactionId}/liability-candidates`)
    ).data;
  }

  async deleteLiabilityAllocation(allocationId: number): Promise<void> {
    await this.client.delete(`/liability-allocation/${allocationId}`);
  }

  async updateLiabilityAllocation(
    allocationId: number,
    data: Omit<LiabilityAllocationRequest, "transaction_id">,
  ): Promise<LiabilityAllocationResponse> {
    return (await this.client.patch(`/liability-allocation/${allocationId}`, data)).data;
  }

  async updateAccount(accountId: number, data: {
    account_name?: string;
    account_type?: string;
  }): Promise<AccountResponse> {
    return (await this.client.patch(`/accounts/${accountId}`, data)).data;
  }

  async deleteAccount(accountId: number): Promise<string> {
    return (await this.client.delete(`/accounts/${accountId}`)).data;
  }

  async deleteCompany(companyId: number): Promise<string> {
    return (await this.client.delete(`/company/${companyId}`)).data;
  }

  async editCompany(companyId: number, data: {
    name: string;
    type_id: number;
    industry_id: number;
    state_id: number;
  }): Promise<CompanyResponse> {
    return (await this.client.patch(`/company/${companyId}`, data)).data;
  }

  async createPlaidLinkSession(
    data: CreatePlaidLinkSessionRequest,
  ): Promise<PlaidLinkSessionOut> {
    return (await this.client.post("/plaid/link-sessions", data)).data;
  }

  async getPlaidLinkSessions(clientId: number): Promise<PlaidLinkSessionOut[]> {
    return (
      await this.client.get("/plaid/link-sessions", { params: { client_id: clientId } })
    ).data;
  }

  async getPlaidItems(clientId: number): Promise<PlaidItemOut[]> {
    return (await this.client.get("/plaid/items", { params: { client_id: clientId } })).data;
  }

  async getPlaidItemAccounts(itemId: number): Promise<PlaidAccountOut[]> {
    return (await this.client.get(`/plaid/items/${itemId}/accounts`)).data;
  }

  async mapPlaidAccounts(
    itemId: number,
    data: PlaidMapAccountsRequest,
  ): Promise<PlaidAccountOut[]> {
    return (await this.client.post(`/plaid/items/${itemId}/map-accounts`, data)).data;
  }

  async syncPlaidItem(itemId: number): Promise<PlaidSyncResponse> {
    return (await this.client.post(`/plaid/items/${itemId}/sync`)).data;
  }

  async deletePlaidItem(itemId: number): Promise<{ success: boolean }> {
    return (await this.client.delete(`/plaid/items/${itemId}`)).data;
  }

  async getPlaidConnectLink(token: string): Promise<PlaidConnectLinkOut> {
    return (await this.client.get(`/plaid/connect/${encodeURIComponent(token)}`)).data;
  }

  async consentPlaidConnectLink(token: string): Promise<PlaidConnectConsentOut> {
    return (
      await this.client.post(`/plaid/connect/${encodeURIComponent(token)}/consent`, {
        agree: true,
      })
    ).data;
  }
}

export const apiClient = new ApiClient();
